import { HttpClient as CapHttpClient } from '@paymash/capacitor-http-plugin';
import { Injectable } from '@angular/core';
import { SendRequestOptions, RequestOptions } from '../../adyen-types';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { StorageKeys } from '@shared/constants/storage.const';
import { LogService } from '@core/services/system/logger';
import { RequestHeaderTypes } from '@shared/constants';
import { PAYMASH_PROFILE } from '@profile';

@Injectable()
export class AdyenApiHelper {
  private readonly logger = this.logService.createLogger('AdyenApiHelper');
  constructor(private readonly localStorage: LocalStorage, private readonly logService: LogService) {}

  get<TResponse>(endpoint: string, options?: RequestOptions) {
    return this.sendRequest<TResponse>({
      endpoint,
      method: 'get',
      errorHandler: options?.errorHandler,
      readTimeout: options?.readTimeout,
    });
  }

  post<TResponse>(endpoint: string, data: unknown, options?: RequestOptions) {
    return this.sendRequest<TResponse>({
      endpoint,
      method: 'post',
      data,
      errorHandler: options?.errorHandler,
      readTimeout: options?.readTimeout,
    });
  }

  private async sendRequest<TResponse>(options: SendRequestOptions): Promise<TResponse> {
    const { endpoint, method, data, errorHandler, readTimeout } = options;
    this.logger.info('Send request', { endpoint, method, data });
    const apiKey = this.getApiKey();
    const headers = this.createHeaders(apiKey);
    const response = await CapHttpClient.sendRequest({
      url: this.getNewEndpoint(endpoint),
      method,
      data,
      headers,
      responseType: 'json',
      readTimeout: readTimeout || PAYMASH_PROFILE.requestTimeout.sendRequest,
    }).catch((error) => {
      if (errorHandler) throw errorHandler(error);
      throw error;
    });
    this.logger.info('Response data', { endpoint, method, data: AdyenApiHelper.responseForLogs(response.data) });
    return response.data;
  }

  /**
   * Strips the Nexo block from a response before it is logged. Only the SoftPOS terminalSettings
   * response carries it, and it holds `encryptionKey.passphrase` — the key used to encrypt every
   * payment request — so it must never reach a log file. Responses without a `nexo` field are
   * returned untouched, which is every other Adyen call including the physical terminals.
   */
  private static responseForLogs<TResponse>(data: TResponse): TResponse | Record<string, unknown> {
    if (!data || typeof data !== 'object' || !('nexo' in data)) {
      return data;
    }

    return { ...(data as Record<string, unknown>), nexo: '***' };
  }

  private createHeaders(apiKey: string) {
    const headers = { [RequestHeaderTypes.ContentType]: 'application/json' };
    if (apiKey) {
      headers[RequestHeaderTypes.ApiKey] = apiKey;
    }
    return headers;
  }

  private getApiKey(): string {
    const apiKey = this.localStorage.get(StorageKeys.paymashPayKey);
    if (!apiKey) throw new Error('Please provide Adyen api key in the settings');
    return apiKey;
  }

  private getNewEndpoint(endpoint: string) {
    const testMercahntIdRegex = /^Paymash_\w+_TEST/g;
    const merchantId = this.localStorage.get(StorageKeys.paymashPayMerchantId);
    if (testMercahntIdRegex.test(merchantId)) {
      return endpoint.replace('-live.adyen.com', '-test.adyen.com');
    }
    return endpoint;
  }
}
